import { get, sum } from 'lodash'

import type {
	EmissionResults,
	FormValues,
	RawMaterialPackagingFormValues,
} from '../Calculator'
import { Data } from './transform'

function calculateEmissionValue(
	optionsdata: Data,
	{
		category,
		subcategory,
		subsubcategory,
		emissionfactor,
		value,
	}: {
		category: string
		subcategory: string
		subsubcategory: string
		emissionfactor: string
		value: number | null
	}
) {
	const emissionvalue = get(
		optionsdata,
		`${category}.${subcategory}.${subsubcategory}.${emissionfactor}`,
		0
	) as number
	return emissionvalue * (value || 0)
}

function calculateRawMaterialPackagingEmissionValue(
	optionsdata: Data,
	{
		category,
		subcategory,
		subsubcategory,
		emissionfactor,
		weight,
		transportcategory,
		transportsubcategory,
		transportsubsubcategory,
		transportemissionfactor,
		distance,
	}: RawMaterialPackagingFormValues
) {
	const rawmaterialsvalue = calculateEmissionValue(optionsdata, {
		category,
		subcategory,
		subsubcategory,
		emissionfactor,
		value: weight ? Number(weight) / 1000 : 0, // convert g to kg
	})

	const transportvalue = calculateEmissionValue(optionsdata, {
		category: transportcategory,
		subcategory: transportsubcategory,
		subsubcategory: transportsubsubcategory,
		emissionfactor: transportemissionfactor,
		value: Number(distance) * (weight ? Number(weight) / 1000 : 0),
	})

	return rawmaterialsvalue + transportvalue
}

function calculateTotalEmissions(
	optionsdata: Data,
	{
		rawmaterial,
		packaging,
		energy,
		transport,
		transportBetweenSites,
	}: FormValues
): EmissionResults {
	const rawmaterialValues = rawmaterial?.map(item =>
		calculateRawMaterialPackagingEmissionValue(optionsdata, item)
	)
	const packagingValues = packaging?.map(item =>
		calculateRawMaterialPackagingEmissionValue(optionsdata, item)
	)
	const energyValues = energy?.map(item =>
		calculateEmissionValue(optionsdata, { ...item, value: Number(item.amount) })
	)
	const transportValues = transport?.map(item =>
		calculateEmissionValue(optionsdata, {
			...item,
			value: Number(item.distance),
		})
	)
	const transportBetweenSitesValues = transportBetweenSites?.map(item =>
		calculateEmissionValue(optionsdata, {
			...item,
			value:
				Number(item.distance) * (item.weight ? Number(item.weight) / 1000 : 0),
		})
	)

	return {
		material: sum(rawmaterialValues) + sum(packagingValues),
		production: sum(energyValues) + sum(transportBetweenSitesValues),
		distribution: sum(transportValues),
	}
}

export { calculateTotalEmissions, sum }
